import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router";
import { LuSmartphone, LuLayoutGrid, LuGauge } from "react-icons/lu";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, companyServices, tech, aiServicePages } from "../controller/global";
import { submitLead } from "../controller/leads";

const parent = companyServices.find((s) => s.key === "web_development");
const facetBlurb = "Mobile-first web design: layouts, tap targets and page weight planned for the phone first, then enhanced for larger screens, so the site works everywhere without a separate mobile version.";

const FEATURE_ROWS = [
  {
    num: "01 / RESPONSIVE",
    Icon: LuSmartphone,
    title: "Built responsive, from the first line of code.",
    copy: "Layouts are designed for the smallest screen first and scale up, so content order, font sizes and tap targets are right on the phones most of your visitors use.",
    tags: ["React", "Next.js", "Vue"],
  },
  {
    num: "02 / FLEXIBLE",
    Icon: LuLayoutGrid,
    title: "One codebase, every screen size.",
    copy: "One responsive codebase serves phones, tablets and desktops, which means no separate mobile site to maintain and no content that exists on one version but not the other.",
    tags: ["Landing pages", "Full-stack apps", "Adaptive layouts"],
  },
  {
    num: "03 / OPTIMIZED",
    Icon: LuGauge,
    title: "Fast on mobile, not just on desktop.",
    copy: "Images, fonts and scripts are sized for mobile networks, so pages load quickly on 4G, which helps both visitors and Google's mobile-first ranking.",
    tags: ["Performance budgets", "Lazy loading", "Core Web Vitals"],
  },
];

const PROCESS_STEPS = [
  { n: "01", t: "Discover", d: "Understand your goals, users, and technical constraints before writing a line of code." },
  { n: "02", t: "Design & Build", d: "UI/UX design and full-stack development, tested on real devices as we go." },
  { n: "03", t: "Test & Launch", d: "Cross-device, cross-browser testing before a confident production rollout." },
  { n: "04", t: "Support", d: "Ongoing maintenance, monitoring, and updates once you're live." },
];

const frontEndTools = tech.find((t) => t.key === "front-end")?.children.map((c) => c.name) ?? [];

const FACTS = [
  { a: "MSME", b: "Registered IT company" },
  { a: "Delhi NCR", b: "Headquarters" },
  { a: "7", b: "Stage delivery process" },
  { a: "11", b: "Core services offered" },
];

const RELATED = [
  { title: "Performance Optimization", path: "/performance-optimization" },
];

const FAQS = [
  {
    q: "What does \"mobile-first\" actually mean?",
    a: "We design and build the mobile layout first, then progressively enhance it for tablet and desktop — rather than designing for desktop and shrinking it down, which is where most responsive bugs come from.",
  },
  {
    q: "Will this work with an existing website?",
    a: "Yes — we can audit an existing site's responsive behavior and fix it, or rebuild specific pages mobile-first without a full redesign.",
  },
  { q: "Why does mobile-first design matter for SEO?", a: "Google ranks sites using their mobile version. A site that is slow or awkward on phones ranks lower, even if it looks good on desktop." },
  { q: "How much traffic comes from mobile?", a: "For most business websites, more than half of visits come from phones. Your analytics will show the exact share for your site." },
  { q: "Do you test on real devices?", a: "Yes. We test on real iOS and Android phones and common screen sizes, not only by resizing a desktop browser." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Mobile-First Design gets its own bespoke page (like /ui-ux-design) instead
// of sharing the generic ServiceFacetPage template used by the other real
// facet pages — same centered hero shape, then genuinely distinct sections
// built from the same real parent-service content (Web Development
// Services) plus the real front-end tech stack shown on /technologies.
const MobileFirstDesign = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Mobile-First Design | InQmise Technologies";

    const description = document.createElement("meta");
    description.name = "description";
    description.content = facetBlurb.slice(0, 160);
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/mobile-first-design");

    const schemaScript = document.createElement("script");
    schemaScript.type = "application/ld+json";
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Mobile-First Design",
      "description": facetBlurb,
      "url": "https://inqmisetechnologies.com/mobile-first-design",
      "provider": { "@id": "https://inqmisetechnologies.com/#organization" },
      "areaServed": ["IN", "US", "GB", "CA", "AU", "AE", "SG"],
      "serviceType": "Mobile-First Design",
    });
    document.head.appendChild(schemaScript);

    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://inqmisetechnologies.com" },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://inqmisetechnologies.com/services" },
        { "@type": "ListItem", "position": 3, "name": parent.title, "item": `https://inqmisetechnologies.com${parent.path}` },
        { "@type": "ListItem", "position": 4, "name": "Mobile-First Design", "item": "https://inqmisetechnologies.com/mobile-first-design" },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  return (
    <main className="editorial-ai pt-20">
      {/* HERO — same split panel shape as /ui-ux-design and the other
          bespoke service pages */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <a
            className="ea-text-link"
            style={{ display: "inline-block", marginBottom: 14 }}
            href={parent.path}
            onClick={(e) => { e.preventDefault(); navigate(parent.path); }}
          >
            ← {parent.title}
          </a>
          <p className="ea-eyebrow">Part of {parent.title}</p>
          <h1>Mobile-first web design</h1>
          <p className="ea-lede">{facetBlurb}</p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={() => setIsOpen(true)} aria-label="Open contact form to start your project">
              Start your project ↗
            </button>
            <a className="ea-text-link" href={parent.path} onClick={(e) => { e.preventDefault(); navigate(parent.path); }}>
              View {parent.title} overview
            </a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / MOBILE-FIRST DESIGN <b>LIVE</b></div>
          <h2><LuSmartphone aria-hidden="true" style={{ verticalAlign: "-4px", marginRight: 8 }} />{parent.description[3]}</h2>
          <div className="ea-query"><small>BUILD</small>{parent.description[1]}</div>
          <div className="ea-query"><small>SCOPE</small>{parent.description[2]}</div>
          <div className="ea-flow"><b>Design</b><i>→</i><b>Build</b><i>→</i><b>Test</b></div>
        </Reveal>
      </header>

      {/* WHY MOBILE-FIRST — feature rows */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Why mobile-first</div>
            <h2>Designed for the<br />smallest screen first.</h2>
          </div>
          <p>Most traffic is mobile — so that's where the design decisions start, not where they end up.</p>
        </Reveal>

        {FEATURE_ROWS.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
      </section>

      {/* OUR PROCESS — alternating vertical timeline */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Our process</div>
              <h2>Four stages,<br />every device tested.</h2>
            </div>
            <p>The same disciplined process behind every service we offer.</p>
          </Reveal>
          <div className="aa-process-line">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="aa-process-row" key={step.n}>
                <div className="aa-process-copy">
                  <h3>{step.t}</h3>
                  <p>{step.d}</p>
                </div>
                <div className="aa-process-node">{step.n}</div>
                <div />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR RESPONSIVE STACK + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">What we build with</div>
              <h2>Real stack,<br />real numbers.</h2>
            </div>
            <p>The same front-end stack shown on /technologies, and the facts behind the team.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Our responsive stack</h3>
              <div className="ea-tags" style={{ padding: "8px 0 0" }}>
                {frontEndTools.map((toolName) => <span key={toolName} style={{ color: "var(--pine)" }}>{toolName}</span>)}
              </div>
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Mobile-first design FAQs</h2>
            <p>Short answers to the questions we hear most.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `mfd-faq-panel-${i}`;
            const buttonId = `mfd-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* EXPLORE MORE — sibling facet + standard cross-links */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Explore more</div>
            <h2>Related to<br />this facet.</h2>
          </div>
          <p>Every facet is part of a real, dedicated service.</p>
        </Reveal>
        <div className="cs-tags-row">
          {RELATED.map((f) => (
            <a
              key={f.path}
              className="cs-tag"
              href={f.path}
              style={{ cursor: "pointer", textDecoration: "none" }}
              onClick={(e) => { e.preventDefault(); navigate(f.path); }}
            >
              {f.title}
            </a>
          ))}
          <a
            className="cs-tag"
            href={parent.path}
            style={{ cursor: "pointer", textDecoration: "none", fontWeight: 700 }}
            onClick={(e) => { e.preventDefault(); navigate(parent.path); }}
          >
            {parent.title} →
          </a>
          <a
            className="cs-tag"
            href="/services"
            style={{ cursor: "pointer", textDecoration: "none" }}
            onClick={(e) => { e.preventDefault(); navigate("/services"); }}
          >
            All Services
          </a>
          <a
            className="cs-tag"
            href="/ai-solutions"
            style={{ cursor: "pointer", textDecoration: "none" }}
            onClick={(e) => { e.preventDefault(); navigate("/ai-solutions"); }}
          >
            AI Solutions
          </a>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="ea-cta" aria-labelledby="mfd-contact-heading">
        <div className="ea-wrap ea-cta-grid">
          <Reveal as="div" reduceMotion={reduceMotion}>
            <div className="ea-eyebrow">A practical first step</div>
            <h2 id="mfd-contact-heading">Ready to go mobile-first?</h2>
            <p>Tell us what you're building and we'll help you scope the right approach.</p>
          </Reveal>

          <Reveal as="div" i={1} reduceMotion={reduceMotion} className="ea-contact-card">
            <span>READY WHEN YOU ARE</span>
            <h3>Start your project.</h3>
            <p>Tell us what you're building and we'll get back to you within one business day.</p>

            <form onSubmit={handleContactSubmit} aria-label="Mobile-First Design project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="mfd-fullname">Full Name</label>
                <input
                  id="mfd-fullname"
                  name="fullname"
                  type="text"
                  value={formData.fullname}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)}
                  aria-describedby={contactErrors.fullname ? "mfd-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="mfd-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="mfd-phone">Phone Number (optional)</label>
                  <input
                    id="mfd-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)}
                    aria-describedby={contactErrors.phone ? "mfd-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="mfd-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="mfd-email">Email</label>
                  <input
                    id="mfd-email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)}
                    aria-describedby={contactErrors.email ? "mfd-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="mfd-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="mfd-subject">Subject</label>
                <input
                  id="mfd-subject"
                  name="subject"
                  type="text"
                  placeholder="e.g., A mobile-first redesign of our site"
                  value={formData.subject}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)}
                  aria-describedby={contactErrors.subject ? "mfd-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="mfd-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="mfd-description">Project Details</label>
                <textarea
                  id="mfd-description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)}
                  aria-describedby={contactErrors.description ? "mfd-description-error" : undefined}
                />
                {contactErrors.description && <p id="mfd-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Submitting…" : "Start a conversation ↗"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default MobileFirstDesign;
